<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  maxImages: {
    type: Number,
    default: 5,
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const fileInput = ref(null);
const uploading = ref(false);
const isDragging = ref(false);
const currentIndex = ref(0);

const images = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const canAddMore = computed(() => images.value.length < props.maxImages);
const hasImages = computed(() => images.value.length > 0);
const currentImage = computed(() => images.value[currentIndex.value]);

const triggerFileInput = () => {
  if (canAddMore.value) {
    fileInput.value?.click();
  }
};

const handleDragOver = e => {
  e.preventDefault();
  if (canAddMore.value) {
    isDragging.value = true;
  }
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const uploadFiles = async files => {
  uploading.value = true;

  try {
    // Convertir les fichiers en data URLs (base64) pour prévisualisation
    const newImages = await Promise.all(
      files.slice(0, props.maxImages - images.value.length).map(
        file =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = e => {
              resolve({
                file,
                url: e.target.result,
                filename: file.name,
                isNew: true,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );

    images.value = [...images.value, ...newImages];
  } catch (error) {
    useAlert(t('WEBSITE.PRODUCTS.IMAGE.UPLOAD_ERROR'));
  } finally {
    uploading.value = false;
  }
};

const handleDrop = async e => {
  e.preventDefault();
  isDragging.value = false;

  if (!canAddMore.value) {
    useAlert(t('WEBSITE.PRODUCTS.IMAGE.MAX_IMAGES_REACHED'));
    return;
  }

  const files = Array.from(e.dataTransfer.files);
  const imageFiles = files.filter(file => file.type.startsWith('image/'));

  if (imageFiles.length > 0) {
    await uploadFiles(imageFiles);
  }
};

const handleFileSelect = async event => {
  const files = Array.from(event.target.files);
  if (files.length > 0) {
    await uploadFiles(files);
  }
  event.target.value = '';
};

const removeImage = index => {
  const newImages = [...images.value];
  newImages.splice(index, 1);
  images.value = newImages;

  if (currentIndex.value >= newImages.length) {
    currentIndex.value = Math.max(0, newImages.length - 1);
  }
};

const previousImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value -= 1;
  }
};

const nextImage = () => {
  if (currentIndex.value < images.value.length - 1) {
    currentIndex.value += 1;
  }
};

const goToImage = index => {
  currentIndex.value = index;
};
</script>

<template>
  <div>
    <label class="block text-sm font-medium text-n-slate-12 mb-2">
      {{ t('WEBSITE.PRODUCTS.IMAGE.LABEL') }}
      <span class="text-xs text-n-slate-10 ml-1">
        ({{ images.length }}/{{ maxImages }})
      </span>
    </label>

    <!-- Zone d'upload et carousel -->
    <div class="space-y-3">
      <!-- Carousel principal -->
      <div
        v-if="hasImages"
        class="relative w-full h-64 bg-n-alpha-1 rounded-lg overflow-hidden border-2 border-n-weak"
      >
        <!-- Image actuelle -->
        <div class="w-full h-full flex items-center justify-center">
          <img
            :src="currentImage.url"
            :alt="currentImage.filename"
            class="max-w-full max-h-full object-contain"
          />
        </div>

        <!-- Contrôles du carousel -->
        <div
          v-if="images.length > 1"
          class="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-2"
        >
          <button
            class="p-2 bg-black/50 hover:bg-black/70 rounded-full transition-all disabled:opacity-30"
            :disabled="currentIndex === 0"
            @click="previousImage"
          >
            <span class="i-lucide-chevron-left size-5 text-white" />
          </button>
          <button
            class="p-2 bg-black/50 hover:bg-black/70 rounded-full transition-all disabled:opacity-30"
            :disabled="currentIndex === images.length - 1"
            @click="nextImage"
          >
            <span class="i-lucide-chevron-right size-5 text-white" />
          </button>
        </div>

        <!-- Bouton supprimer -->
        <button
          class="absolute top-2 right-2 p-2 bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-lg transition-all"
          :title="t('WEBSITE.PRODUCTS.IMAGE.REMOVE')"
          @click="removeImage(currentIndex)"
        >
          <span class="i-lucide-trash-2 size-4" />
        </button>

        <!-- Indicateurs -->
        <div
          v-if="images.length > 1"
          class="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5"
        >
          <button
            v-for="(image, index) in images"
            :key="index"
            class="w-2 h-2 rounded-full transition-all"
            :class="[
              index === currentIndex
                ? 'bg-white w-6'
                : 'bg-white/50 hover:bg-white/80',
            ]"
            @click="goToImage(index)"
          />
        </div>
      </div>

      <!-- Zone d'upload -->
      <div
        v-if="canAddMore"
        class="border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all py-8"
        :class="[
          isDragging
            ? 'border-n-blue-9 bg-n-blue-2 scale-[0.98]'
            : 'border-n-weak hover:border-n-blue-6 hover:bg-n-blue-2/30',
        ]"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
        @click="triggerFileInput"
      >
        <div v-if="uploading" class="flex flex-col items-center gap-2">
          <Spinner />
          <span class="text-sm text-n-slate-11">{{
            t('WEBSITE.UPLOADER.UPLOADING')
          }}</span>
        </div>
        <div v-else class="flex flex-col items-center gap-2">
          <span class="i-lucide-images size-8 text-n-slate-10" />
          <span class="text-sm text-center text-n-slate-11">
            {{ t('WEBSITE.PRODUCTS.IMAGE.DROP_OR_CLICK') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('WEBSITE.PRODUCTS.IMAGE.MAX_SIZE') }}
          </span>
        </div>
      </div>

      <!-- Miniatures -->
      <div v-if="images.length > 1" class="flex gap-2 overflow-x-auto pb-2">
        <button
          v-for="(image, index) in images"
          :key="index"
          class="flex-shrink-0 w-16 h-16 rounded-lg border-2 transition-all overflow-hidden"
          :class="[
            index === currentIndex
              ? 'border-n-blue-9 ring-2 ring-n-blue-9/30'
              : 'border-n-weak hover:border-n-blue-6',
          ]"
          @click="goToImage(index)"
        >
          <img
            :src="image.url"
            :alt="image.filename"
            class="w-full h-full object-cover"
          />
        </button>
      </div>
    </div>

    <!-- Input file caché -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      multiple
      class="hidden"
      @change="handleFileSelect"
    />
  </div>
</template>
